import { useState, useRef, useCallback } from "react";
import { ImageCropDialog } from "@/components/ImageCropDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Upload, Crop } from "lucide-react";
import { useCategories, useCreateCategory } from "@/hooks/useCategories";
import { useCreateMenuItem, useUpdateMenuItem, type MenuItemRow } from "@/hooks/useMenuItems";
import { useDietaryTags } from "@/hooks/useDietaryTags";
import { usePricingTypes } from "@/hooks/usePricingTypes";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { extensionFor } from "@/lib/image-compress";
import { LONG_CACHE_CONTROL } from "@/lib/storage";

interface MenuItemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: MenuItemRow | null;
}

export function MenuItemFormDialog({ open, onOpenChange, item }: MenuItemFormDialogProps) {
  const { data: categories } = useCategories();
  const { data: pricingTypes } = usePricingTypes();
  const { data: dietaryTags } = useDietaryTags();
  const create = useCreateMenuItem();
  const update = useUpdateMenuItem();
  const createCategory = useCreateCategory();
  const isEdit = !!item;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [quickAddCategory, setQuickAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [categoryId, setCategoryId] = useState(item?.category_id ?? "");
  const [isActive, setIsActive] = useState(item?.is_active ?? true);
  const [tags, setTags] = useState<string[]>(item?.dietary_tags ?? []);
  const [sku, setSku] = useState(item?.sku ?? "");
  const [abbreviation, setAbbreviation] = useState(item?.abbreviation ?? "");
  const [imageUrl, setImageUrl] = useState(item?.image_url ?? "");
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [prices, setPrices] = useState<{ pricing_type: string; price: number; label: string; serves: number | null; sku: string }[]>(
    item?.prices?.map((p) => ({ pricing_type: p.pricing_type, price: p.price, label: p.label ?? "", serves: p.serves, sku: p.sku ?? "" })) ?? [{ pricing_type: "per_plate", price: 0, label: "", serves: null, sku: "" }]
  );

  const toggleTag = (tag: string) => setTags((t) => (t.includes(tag) ? t.filter((x) => x !== tag) : [...t, tag]));

  const addPrice = () => setPrices((p) => [...p, { pricing_type: "per_plate", price: 0, label: "", serves: null, sku: "" }]);
  const removePrice = (i: number) => setPrices((p) => p.filter((_, idx) => idx !== i));
  const updatePrice = (i: number, field: string, value: string | number | null) =>
    setPrices((p) => p.map((pr, idx) => (idx === i ? { ...pr, [field]: value } : pr)));

  const uploadBlob = async (blob: Blob) => {
    setUploading(true);
    try {
      // The crop is WebP, or JPEG/PNG where the browser can't encode WebP (Safari).
      const ext = extensionFor(blob.type) ?? "webp";
      const path = `menu-items/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("website-assets").upload(path, blob, { upsert: true, cacheControl: LONG_CACHE_CONTROL, contentType: blob.type || "image/webp" });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from("website-assets").getPublicUrl(path);
      setImageUrl(urlData.publicUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error((err as Error).message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const startCropFromFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = useCallback((croppedBlob: Blob) => {
    setCropSrc(null);
    uploadBlob(croppedBlob);
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) startCropFromFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) startCropFromFile(file);
  };

  const handleCropExisting = () => {
    if (imageUrl) {
      setCropSrc(imageUrl);
      setCropOpen(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !categoryId) return;
    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      category_id: categoryId,
      is_active: isActive,
      dietary_tags: tags,
      sku: sku.trim() || undefined,
      abbreviation: abbreviation.trim() || undefined,
      image_url: imageUrl || null,
      prices: prices.filter((p) => p.price > 0).map((p) => ({
        pricing_type: p.pricing_type,
        price: p.price,
        label: p.label || undefined,
        serves: p.serves ?? undefined,
        sku: p.sku || undefined,
      })),
    };
    const mutation = isEdit
      ? update.mutateAsync({ id: item!.id, ...payload })
      : create.mutateAsync(payload);
    // The hook's onError already shows the toast; keep the dialog open on failure.
    mutation.then(() => onOpenChange(false)).catch(() => {});
  };

  const isSynced = !!item?.square_catalog_id;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>{isEdit ? "Edit Menu Item" : "Add Menu Item"}</DialogTitle>
            {isEdit && (
              <Badge variant={isSynced ? "default" : "secondary"} className="text-xs">
                {isSynced ? "✓ Synced to Square" : "Not synced"}
              </Badge>
            )}
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Image Drop Zone */}
          <div className="space-y-2">
            <Label>Image</Label>
            <div
              className={`relative rounded-md border-2 border-dashed transition-colors cursor-pointer ${
                isDragging
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "border-muted-foreground/25 hover:border-muted-foreground/50"
              } ${imageUrl ? "p-2" : "p-6"}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={handleDrop}
            >
              {imageUrl ? (
                <div className="flex items-center gap-3">
                  <div className="relative h-20 w-20 rounded-md overflow-hidden border bg-muted shrink-0">
                    <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setImageUrl(""); }}
                      className="absolute top-0 right-0 bg-background/80 rounded-bl p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleCropExisting(); }}
                      className="absolute bottom-0 right-0 bg-background/80 rounded-tl p-0.5"
                      title="Crop image"
                    >
                      <Crop className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {isDragging ? "Drop to replace" : "Drop image to replace, or click to browse"}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Upload className="h-8 w-8 mb-1" />
                  <span className="text-sm font-medium">
                    {isDragging ? "Drop image here" : uploading ? "Uploading..." : "Drop image here or click to upload"}
                  </span>
                  <span className="text-xs">PNG, JPG, WEBP</span>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chicken Parmigiana" required />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the dish..." rows={2} />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {categories?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!quickAddCategory ? (
              <button type="button" className="text-xs text-primary hover:underline" onClick={() => setQuickAddCategory(true)}>
                + New category
              </button>
            ) : (
              <div className="flex gap-2 items-center">
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Category name"
                  className="h-8 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (newCategoryName.trim()) {
                        createCategory.mutate(
                          { name: newCategoryName.trim() },
                          { onSuccess: (data) => { setCategoryId(data.id); setNewCategoryName(""); setQuickAddCategory(false); } }
                        );
                      }
                    }
                    if (e.key === "Escape") { setQuickAddCategory(false); setNewCategoryName(""); }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  className="h-8"
                  disabled={!newCategoryName.trim() || createCategory.isPending}
                  onClick={() => {
                    if (newCategoryName.trim()) {
                      createCategory.mutate(
                        { name: newCategoryName.trim() },
                        { onSuccess: (data) => { setCategoryId(data.id); setNewCategoryName(""); setQuickAddCategory(false); } }
                      );
                    }
                  }}
                >
                  Add
                </Button>
                <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => { setQuickAddCategory(false); setNewCategoryName(""); }}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>

          {/* SKU & Abbreviation */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>SKU</Label>
              <Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="e.g. CHKN-PRM" />
            </div>
            <div className="space-y-2">
              <Label>Abbreviation</Label>
              <Input value={abbreviation} onChange={(e) => setAbbreviation(e.target.value.slice(0, 24))} placeholder="POS label (max 24)" maxLength={24} />
              <p className="text-xs text-muted-foreground">{abbreviation.length}/24 — shown on Square POS</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Dietary Tags</Label>
            <div className="flex flex-wrap gap-2">
              {dietaryTags?.map((dt) => (
                <Badge
                  key={dt.key}
                  variant={tags.includes(dt.key) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleTag(dt.key)}
                >
                  {dt.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Pricing</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addPrice}>
                <Plus className="h-3 w-3 mr-1" /> Add Price
              </Button>
            </div>
            {prices.map((p, i) => (
              <div key={i} className="space-y-2 rounded-md border p-3">
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Type</Label>
                    <Select value={p.pricing_type} onValueChange={(v) => updatePrice(i, "pricing_type", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {pricingTypes?.map((pt) => (
                          <SelectItem key={pt.key} value={pt.key}>{pt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Label className="text-xs">Price</Label>
                    <Input type="number" step="0.01" min="0" placeholder="0.00" value={p.price || ""} onChange={(e) => updatePrice(i, "price", parseFloat(e.target.value) || 0)} />
                  </div>
                  {prices.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePrice(i)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Label</Label>
                    <Input placeholder="e.g. Full Pan" value={p.label} onChange={(e) => updatePrice(i, "label", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Serves</Label>
                    <Input type="number" min="1" placeholder="—" value={p.serves ?? ""} onChange={(e) => updatePrice(i, "serves", e.target.value ? parseInt(e.target.value) : null)} />
                  </div>
                  <div>
                    <Label className="text-xs">Variation SKU</Label>
                    <Input placeholder="SKU" value={p.sku} onChange={(e) => updatePrice(i, "sku", e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>Active</Label>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={create.isPending || update.isPending}>
              {isEdit ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    {cropSrc && (
      <ImageCropDialog
        open={cropOpen}
        onOpenChange={(o) => { setCropOpen(o); if (!o) setCropSrc(null); }}
        imageSrc={cropSrc}
        onCropComplete={handleCropComplete}
        aspect={1}
      />
    )}
    </>
  );
}
